import React, { cloneElement, useEffect, useMemo, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { Image } from '../common/image';
import { Switch } from '../ui/switch';
import { CheckboxCheckedGreenIcon } from './assets/checkbox-checked-green-icon';
import { CheckboxOutlineIcon } from './assets/checkbox-outline-icon';
import { RadioHalfBlackIcon } from './assets/radio-half-black-icon';
import { RadioCheckedBlackIcon } from './assets/radio-checked-black-icon';
import { FolderSparkleIcon } from '../assets-material-item/assets/icon/folder-sparkle-icon';
import { TagItem } from '../assets-material-item/tag-item';
import { LoadingOverlay } from '../common/loading-overlay';
import { getSceneByType, VIDEO_TEMPLATE_SCENE, VIDEO_TEMPLATE_TYPE } from '@/manager/manager/agent-user-template-list-manager';
import { MaterialConfigEmptyImage } from './assets/material-config-empty';
import { useToast } from '@/hooks/use-toast';
import { TemplateMaterialSettingsData } from '@/services/agent-material-settings';
import { RadioOutlineIcon } from './assets/radio-outline-icon';

interface MaterialRangeSettingModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  /** 视频模板类型 */
  scene?: VIDEO_TEMPLATE_TYPE;
  /** 启用素材 */
  enabledMaterial?: boolean;
  /** 启用笔记素材 */
  enabledNote?: boolean;
  onOpenChange?: (open: boolean) => void;

  // ======== 素材配置数据 =========
  openUse: boolean,
  openPublic: boolean,
  openPrivate: boolean,
  openNote: boolean,
  setOpenUse: (open: boolean) => void,
  setOpenPublic: (open: boolean) => void,
  setOpenPrivate: (open: boolean) => void,
  setOpenNote: (open: boolean) => void,
  // 个人素材文件夹和标签配置
  hasTags: boolean,
  isRelatedMaterial: boolean,
  personalConfigs: TemplateMaterialSettingsData['personalConfigs'],
  filterPersonalConfigs: TemplateMaterialSettingsData['personalConfigs'],
  selectedTagCount: number,
  loading: boolean,
  updating: boolean,
  toggleSelectTag: (tagId: number) => void,
  toggleSelectTagAll: (folderId: number, checked: boolean) => void,
  updateMaterialSettings: () => Promise<void>,
}

/** 素材范围设置弹窗 */
export const MaterialRangeSettingModal: React.FC<MaterialRangeSettingModalProps> = ({
  trigger,
  open: externalOpen,
  scene,
  enabledMaterial,
  enabledNote,
  onOpenChange,

  // 打开状态
  openUse,
  openPublic,
  openPrivate,
  openNote,
  setOpenUse,
  setOpenPublic,
  setOpenPrivate,
  setOpenNote,
  // 个人素材文件夹和标签配置
  hasTags,
  isRelatedMaterial,
  personalConfigs,
  toggleSelectTag,
  toggleSelectTagAll,
  filterPersonalConfigs,
  selectedTagCount,
  loading,
  updating,
  updateMaterialSettings,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const titleRef = useRef<HTMLHeadingElement>(null); // 用于聚焦标题的ref
  const [internalOpen, setInternalOpen] = useState(false);

  const isTriggerMode = !!trigger;
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  const [selectedFolderId, setSelectedFolderId] = useState<number>(0);

  // 视频模板类型对应的场景
  const videoTemplateScene = getSceneByType(scene);

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  const handleSave = async () => {
    if (enabledMaterial) {
      const valid = validateSubmit();
      if (!valid) return;

      await updateMaterialSettings();
    };
    handleClose();
  }

  // 保存按钮是否禁用
  const disabledSave = useMemo(() => {
    if (videoTemplateScene === VIDEO_TEMPLATE_SCENE.MIXCUT) {
      // 有笔记时：判断是否选择了公共素材或笔记素材
      if (enabledNote) {
        return !openPublic && !openNote;
      } else {
        // 无笔记时：判断是否选择了公共素材或个人素材
        return !openPublic && !openPrivate;
      }
    }
  }, [videoTemplateScene, openPublic, openPrivate, openNote])

  // 校验提交
  const validateSubmit = () => {
    // 混剪体需要选择公共素材或个人素材
    if (disabledSave) {
      toast.default({
        description: t('template_material_range.modal.validate_material_toast'),
      })
      return false;
    }

    // 非笔记素材范围：勾选个人素材，且素材有标签，则必须选择标签
    if (!enabledNote && openPrivate && hasTags) {
      const hasSelectedTag = personalConfigs.some(folder => folder.tags?.some(tag => tag.isSelected));
      if (!hasSelectedTag) {
        toast.default({ description: t('template_material_range.modal.validate_tags_toast') })
        return false;
      }
    }

    return true;
  }

  // 切换使用素材
  const handleToggleOpenUse = (checked: boolean) => {
    if (!enabledMaterial) {
      toast.default({
        description: t('template_material_range.modal.no_need_material_toast'),
      })
      return;
    };

    setOpenUse(checked);
  }

  // 切换个人素材
  const handleToggleOpenPrivate = () => {
    if (!openUse) return;
    if (!isRelatedMaterial) {
      toast.default({
        description: t('template_material_range.modal.no_related_folder_toast'),
      })
      return;
    }

    setOpenPrivate(!openPrivate)
  }

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  // 过滤当前选中文件夹的标签
  const filterTags = useMemo(() => {
    const config = personalConfigs?.find((item) => item.folderId === selectedFolderId);
    return config?.tags || [];
  }, [selectedFolderId, personalConfigs])

  // 是否可以选择素材范围（选择标签）
  const enableSelectTag = openUse && openPrivate;

  // 选择左侧文件夹
  const handleSelectFolderId = (folderId: number, hasTags: boolean) => {
    if (!enableSelectTag) return;

    if (!hasTags) {
      toast.default({ description: t('template_material_range.modal.empty_tags_toast') })
      return;
    };

    setSelectedFolderId(folderId)
  }

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "relative flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[700px] h-[680px] max-w-[70%] max-h-[80%] flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-semibold flex items-center'
          >
            <span>{t('template_material_range.modal.title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className={cn(
          'relative flex flex-col px-6 pt-5 select-none overflow-hidden',
          (enabledMaterial && !enabledNote) && 'flex-1'
        )}>
          <div>
            <p className='text-sm text-[rgba(16,24,40,1)]'>{t('template_material_range.modal.label')}</p>
            <span className='text-xs text-[rgba(152,162,179,1)]'>{t('template_material_range.modal.desc')}</span>
          </div>
          <div className={cn(
            'relative mt-5 p-5 flex flex-col rounded-[16px] bg-[rgba(249,250,251,1)] overflow-y-auto flex-1 h-full',
          )}>
            {/* 使用素材 */}
            <div className={cn(
              'flex justify-between items-center',
              enabledMaterial && 'pb-5 border-b border-[rgba(234,236,240,1)]',
              !enabledMaterial && '!opacity-50',
            )}>
              <label className='text-sm text-[rgba(16,24,40,1)]'>{t('template_material_range.modal.use_material')}</label>
              <Switch
                className={cn(
                  'data-[state=checked]:bg-[rgba(18,183,106,1)]',
                  !enabledMaterial && 'cursor-not-allowed',
                )}
                checked={openUse}
                onCheckedChange={handleToggleOpenUse}
              />
            </div>
            {enabledMaterial && (
              <div className={cn('pl-[14px]', !openUse && '!opacity-50 cursor-not-allowed')}>
                {/* 公共素材 */}
                <div className='flex justify-between items-center border-b border-[rgba(234,236,240,1)] py-5'>
                  <label className='text-sm text-[rgba(16,24,40,1)]'>{t('template_material_range.modal.public_material')}</label>
                  <CustomCheckbox
                    className='pr-2'
                    checked={openPublic}
                    disabled={!openUse}
                    onClick={() => {
                      if (!openUse) return;
                      setOpenPublic(!openPublic)
                    }}
                  />
                </div>
                {/* 笔记素材 */}
                {enabledNote && (
                  <div className='flex justify-between items-center pt-5'>
                    <label className='text-sm text-[rgba(16,24,40,1)]'>{t('template_material_range.modal.note_material')}</label>
                    <CustomCheckbox
                      className='pr-2'
                      checked={openNote}
                      disabled={!openUse}
                      onClick={() => {
                        if (!openUse) return;
                        setOpenNote(!openNote)
                      }}
                    />
                  </div>
                )}
                {/* 个人素材 */}
                {!enabledNote && (
                  <div className='h-full py-5'>
                    <div className={cn(
                      'w-full',
                      !isRelatedMaterial && '!opacity-50 cursor-not-allowed',
                    )}>
                      <div className='flex justify-between items-center '>
                        <label className='text-sm text-[rgba(16,24,40,1)]'>{t('template_material_range.modal.private_material')}</label>
                        <CustomCheckbox
                          className='pr-2'
                          // 有关联素材按状态显示，否则不显示
                          checked={isRelatedMaterial ? openPrivate : false}
                          disabled={!openUse || !isRelatedMaterial}
                          onClick={handleToggleOpenPrivate}
                        />
                      </div>
                      <div className='text-xs pt-1 text-[rgba(152,162,179,1)]'>
                        {isRelatedMaterial ? t('template_material_range.modal.selected_tag', { count: selectedTagCount }) : t('template_material_range.modal.no_related_folder')}
                      </div>
                    </div>
                    {/* 素材库文件夹标签 */}
                    {isRelatedMaterial && (
                      <div className={cn(
                        'flex h-full mt-4',
                        !enableSelectTag && 'opacity-50 cursor-not-allowed',
                      )}>
                        {/* 左侧文件夹导航 */}
                        <div className={cn(
                          'flex-1 flex flex-col gap-1.5 max-w-max pr-[15.5px] border-r border-[rgba(234,236,240,1)]',
                          'overflow-y-auto min-w-max',
                        )}>
                          <div className={cn(
                            'w-[200px] rounded-[6px] px-3 py-2 cursor-pointer flex items-center justify-between',
                            selectedFolderId === 0 && 'bg-[rgba(16,24,40,0.05)]',
                            !enableSelectTag && 'cursor-not-allowed',
                          )}
                            onClick={() => {
                              if (!enableSelectTag) return;
                              setSelectedFolderId(0)
                            }}
                          >
                            <span className='text-sm text-[rgba(16,24,40,1)]'>{t('template_material_range.modal.selected_tab')}</span>
                            <span className='text-xs text-[rgba(102,112,133,1)]'>{t('template_material_range.modal.selected_tag', { count: selectedTagCount })}</span>
                          </div>
                          {personalConfigs.map((item) => {
                            // 计算状态
                            const isAllSelected = item.selectTotal === item.tags.length && item.tags.length > 0;
                            const isIndeterminate = item.selectTotal > 0 && item.selectTotal < item.tags.length;

                            return (
                              <div
                                key={item.folderId}
                                className={cn(
                                  'w-[200px] max-w-[200px] gap-[11px] flex items-center rounded-[6px] px-3 py-2 cursor-pointer',
                                  selectedFolderId === item.folderId && 'bg-[rgba(16,24,40,0.05)]',
                                  !enableSelectTag && 'cursor-not-allowed',
                                  item.tags.length === 0 && 'opacity-50 cursor-not-allowed',
                                )}
                                onClick={() => handleSelectFolderId(item.folderId, item.tags.length > 0)}
                              >
                                <TriStateCheckbox
                                  checked={isAllSelected}
                                  indeterminate={isIndeterminate}
                                  disabled={!enableSelectTag || !item.tags.length}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!enableSelectTag) return;
                                    toggleSelectTagAll(item.folderId, !isAllSelected);
                                  }}
                                />
                                <div className='flex w-full min-w-0 items-center justify-between'>
                                  <div className='flex w-full items-center gap-1.5'>
                                    <FolderSparkleIcon className="size-5 flex-shrink-0" />
                                    <span className='text-sm text-[rgba(16,24,40,1)] max-w-[105px] truncate'>
                                      {item.folderName}
                                    </span>
                                  </div>
                                  <span className='text-xs text-[rgba(102,112,133,1)] self-end'>
                                    {item.selectTotal}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {/* 右侧标签内容 */}
                        <div className='w-full pl-[15.5px]'>
                          {/* 已选中的标签 */}
                          {selectedFolderId === 0 && (
                            <div className='w-full flex flex-1 flex-col gap-5 overflow-y-auto'>
                              {filterPersonalConfigs.length > 0 ? filterPersonalConfigs?.map((item) => (
                                <div key={item.folderId} className='w-full flex flex-col gap-3'>
                                  <div className='w-full text-[rgba(16,24,40,1)] text-sm flex items-center justify-between'>
                                    <div className='flex items-center gap-1.5'>
                                      <FolderSparkleIcon className="size-5" />
                                      <span>{item.folderName}</span>
                                    </div>
                                    <span
                                      className={cn(
                                        'text-xs text-[rgba(152,162,179,1)] cursor-pointer hover:text-[rgba(102,112,133,1)] transition-colors',
                                        !enableSelectTag && 'cursor-not-allowed',
                                      )}
                                      onClick={() => {
                                        if (!enableSelectTag) return;
                                        toggleSelectTagAll(item.folderId, false)
                                      }}
                                    >{t('template_material_range.modal.clear')}</span>
                                  </div>
                                  <div className='flex flex-wrap gap-[10px]'>
                                    {item.tags.map((tag) => (
                                      <TagItem
                                        key={tag.tagId}
                                        active={tag.isSelected}
                                        disabled={!enableSelectTag}
                                        onClick={() => {
                                          if (!enableSelectTag) return;
                                          toggleSelectTag(tag.tagId)
                                        }}
                                      >
                                        {tag.tagName}
                                      </TagItem>
                                    ))}
                                  </div>
                                </div>
                              )) : (
                                <EmptyState />
                              )}
                            </div>
                          )}
                          {/* 每个文件夹对应的的标签 */}
                          {selectedFolderId !== 0 && (
                            <div className='w-full flex flex-wrap gap-[10px]'>
                              {filterTags.length ? filterTags?.map((tag) => (
                                <TagItem
                                  key={tag.tagId}
                                  active={tag.isSelected}
                                  onClick={() => {
                                    if (!enableSelectTag) return;
                                    toggleSelectTag(tag.tagId)
                                  }}
                                >
                                  {tag.tagName}
                                </TagItem>
                              )) : (
                                <EmptyState />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <LoadingOverlay loading={loading || updating} />
        </div>
        {/* 操作按钮区域 */}
        <div className='w-full h-[93px] flex items-center justify-end p-6 z-20 select-none mt-auto'>
          <div className='flex items-center gap-2'>
            <TextButton
              label={t('agent_dashboard.user_detail.voice.add_modal.save')}
              className={cn(
                'w-[135px] h-11',
                disabledSave && 'opacity-50 cursor-not-allowed',
              )}
              onClick={handleSave}
              disabled={loading || updating}
            />
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}

// 自定义复选框组件
const CustomCheckbox = ({
  checked,
  onClick,
  className,
  disabled,
}: {
  checked: boolean;
  className?: string;
  disabled?: boolean;
  onClick: () => void;
}) => {
  return (
    <div
      className={cn(
        'cursor-pointer',
        disabled && 'cursor-not-allowed',
        className
      )}
      onClick={onClick}
    >
      {checked ? <CheckboxCheckedGreenIcon /> : <CheckboxOutlineIcon />}
    </div>
  );
}

interface TriStateCheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
}

// 三状态复选框组件
const TriStateCheckbox: React.FC<TriStateCheckboxProps> = ({
  checked,
  indeterminate,
  disabled,
  onClick,
  className,
}) => {
  // 根据状态决定渲染哪个图标
  const renderIcon = () => {
    if (disabled && !checked && !indeterminate) {
      return <RadioOutlineIcon className="flex-shrink-0 size-[18px]" />;
    }

    if (indeterminate) {
      return <RadioHalfBlackIcon className="flex-shrink-0 active:scale-90 hover:text-black/70 transition-all" />;
    }

    if (checked) {
      return <RadioCheckedBlackIcon className="flex-shrink-0 active:scale-90 hover:text-black/70 transition-all" />;
    }

    return <RadioOutlineIcon className="flex-shrink-0 size-[18px] active:scale-90 transition-all" />;
  };

  return (
    <div
      className={cn(
        "flex items-center justify-center transition-opacity",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        className
      )}
      onClick={(e) => {
        if (disabled) return;
        onClick?.(e);
      }}
    >
      {renderIcon()}
    </div>
  );
};

// 空状态组件
const EmptyState = ({
  className,
}: { className?: string }) => {
  const { t } = useTranslation();
  return (
    <div className={cn('flex w-full pt-8 flex-col justify-center items-center', className)}>
      <MaterialConfigEmptyImage className='w-[100px]' />
      <div className='text-[rgba(152,162,179,1)] text-xs'>{t('template_material_range.modal.empty_content')}</div>
    </div>
  )
}